import React, { useState, useEffect } from 'react';
import { ApiServiceAdmin } from '../services/ApiServiceAdmin';
import { useAssociation } from '../contexts/AssociationContext';
import { PRESET_THEMES, AppTheme, DEFAULT_THEME, SavedCustomTheme } from '../theme/themes';
import { SystemParamsTab } from './SystemParamsTab';
import { SubjectsSettingsTab } from './SubjectsSettingsTab';
import { LandingSectionsTab } from './LandingSectionsTab';

// Onglets réservés au superadmin uniquement
const SUPER_ADMIN_TABS = new Set(['basic', 'subjects']);

interface Props {
  currentLanguage: string;
  isRTL: boolean;
  canEdit?: boolean;
}

interface AssocForm {
  name:             { ar: string; fr: string; en: string };
  subtitle:         { ar: string; fr: string; en: string };
  team_name:        { ar: string; fr: string; en: string };
  subject:          { ar: string; fr: string; en: string };
  keywords:         { ar: string; fr: string; en: string };
  logo_url:         string;
  og_image:         string;
  website:          string;
  support_email:    string;
  default_language: string;
  theme:            AppTheme;
}

const EMPTY: AssocForm = {
  name:             { ar: '', fr: '', en: '' },
  subtitle:         { ar: '', fr: '', en: '' },
  team_name:        { ar: '', fr: '', en: '' },
  subject:          { ar: '', fr: '', en: '' },
  keywords:         { ar: '', fr: '', en: '' },
  logo_url:         '',
  og_image:         '',
  website:          '',
  support_email:    '',
  default_language: 'ar',
  theme:            DEFAULT_THEME,
};

const api = ApiServiceAdmin.getInstance();


type LangTriple = { ar: string; fr: string; en: string };

function legacyLangTriple(d: Record<string, unknown>, legacyKey: string): LangTriple {
  return {
    ar: String(d[`${legacyKey}_ar`] ?? ''),
    fr: String(d[`${legacyKey}_fr`] ?? ''),
    en: String(d[`${legacyKey}_en`] ?? ''),
  };
}

function normalizeLangField(raw: unknown, d?: Record<string, unknown>, legacyKey?: string): LangTriple {
  const leg =
    d && legacyKey ? legacyLangTriple(d, legacyKey) : { ar: '', fr: '', en: '' };

  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    return {
      ar: String(o.ar ?? '').trim() || leg.ar,
      fr: String(o.fr ?? '').trim() || leg.fr,
      en: String(o.en ?? '').trim() || leg.en,
    };
  }
  if (typeof raw === 'string' && raw.trim()) {
    if (leg.ar || leg.fr || leg.en) return leg;
    return { ar: raw, fr: raw, en: raw };
  }
  return leg;
}

function normalizeKeywordsField(raw: unknown): LangTriple {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    return { ar: String(o.ar ?? ''), fr: String(o.fr ?? ''), en: String(o.en ?? '') };
  }
  if (typeof raw === 'string') {
    return { ar: raw, fr: raw, en: raw };
  }
  return { ar: '', fr: '', en: '' };
}

function normalizeDefaultLanguage(raw: unknown): 'ar' | 'fr' | 'en' {
  let v: unknown = raw;
  if (typeof v === 'string' && /^".*"$/.test(v.trim())) {
    try {
      v = JSON.parse(v);
    } catch {
      /* keep string */
    }
  }
  const s = String(v ?? 'ar').toLowerCase().trim();
  if (s === 'ar' || s === 'fr' || s === 'en') return s;
  return 'ar';
}

// ── Color slot labels ──────────────────────────────────────────────────────────

const COLOR_SLOTS: { key: keyof AppTheme; labelFr: string; labelAr: string; hint?: string }[] = [
  { key: 'primary',     labelFr: 'Couleur principale',        labelAr: 'اللون الأساسي',           hint: 'Boutons, liens, éléments interactifs' },
  { key: 'primaryText', labelFr: 'Texte sur fond principal',  labelAr: 'نص على الخلفية الأساسية', hint: 'Texte des boutons (blanc ou noir)' },
  { key: 'headerBg',    labelFr: 'Fond du header',            labelAr: 'خلفية الرأسية',           hint: 'Arrière-plan de la barre de navigation' },
  { key: 'headerText',  labelFr: 'Texte du header',           labelAr: 'نص الرأسية',              hint: 'Texte et icônes dans le header' },
  { key: 'accent',      labelFr: 'Couleur d\'accent',         labelAr: 'لون التمييز',             hint: 'Badges, indicateurs actifs, soulignements' },
  { key: 'background',  labelFr: 'Fond de page',              labelAr: 'خلفية الصفحة',            hint: 'Arrière-plan global de l\'application' },
  { key: 'surface',     labelFr: 'Fond des cartes',           labelAr: 'خلفية البطاقات',          hint: 'Fond des panneaux et cartes de contenu' },
  { key: 'borderColor', labelFr: 'Couleur des bordures',      labelAr: 'لون الحدود',              hint: 'Bordures, séparateurs, contours' },
  { key: 'textColor',   labelFr: 'Couleur de police',         labelAr: 'لون الخط الرئيسي',        hint: 'Texte principal du corps de la page' },
];

export const AssociationSettings: React.FC<Props> = ({ currentLanguage, isRTL, canEdit = false }) => {
  const { refresh } = useAssociation();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isMobile = windowWidth < 640;

  const [form, setForm]             = useState<AssocForm>(EMPTY);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [message, setMessage]       = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeTab, setActiveTab]   = useState<'basic' | 'theme' | 'system' | 'subjects' | 'landing'>(() => {
    // Éviter de démarrer sur un onglet interdit pour les admins BDD
    try {
      const user = JSON.parse(localStorage.getItem('app_user') || '{}');
      if (user.isSuperUser !== true) return 'theme';
    } catch {}
    return 'basic';
  });
  const [fieldLang, setFieldLang]   = useState<'ar' | 'fr' | 'en'>('ar'); // onglet langue actif dans l'onglet basic
  const [savedThemes, setSavedThemes]       = useState<SavedCustomTheme[]>([]);
  const [editingSavedId, setEditingSavedId] = useState<string | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveDialogName, setSaveDialogName] = useState('');

  const lbl = (fr: string, ar: string) => isRTL ? ar : fr;

  // Vérifier si l'utilisateur est le superadmin
  const isSuperAdmin = (() => {
    try {
      const user = JSON.parse(localStorage.getItem('app_user') || '{}');
      return user.isSuperUser === true;
    } catch {
      return false;
    }
  })();

  // Filtrer les onglets : les admins BDD ne voient que theme et system
  const visibleTabs = isSuperAdmin
    ? ['basic', 'theme', 'system', 'subjects', 'landing']
    : ['theme', 'system', 'landing'];

  // Rediriger vers un onglet autorisé si l'onglet actif est interdit
  useEffect(() => {
    if (!isSuperAdmin && SUPER_ADMIN_TABS.has(activeTab)) {
      setActiveTab('theme');
    }
  }, [activeTab, isSuperAdmin]);

  useEffect(() => {
    api.getAssociationParams().then((res: any) => {
      if (res?.success && res.data) {
        const d = res.data;
        const rawTheme = d.theme;
        const theme: AppTheme = rawTheme && rawTheme.primary ? {
          id:          rawTheme.id          ?? null,
          primary:     rawTheme.primary     ?? DEFAULT_THEME.primary,
          primaryText: rawTheme.primaryText ?? DEFAULT_THEME.primaryText,
          headerBg:    rawTheme.headerBg    ?? DEFAULT_THEME.headerBg,
          headerText:  rawTheme.headerText  ?? DEFAULT_THEME.headerText,
          accent:      rawTheme.accent      ?? DEFAULT_THEME.accent,
          surface:     rawTheme.surface     ?? DEFAULT_THEME.surface,
          textColor:   rawTheme.textColor   ?? DEFAULT_THEME.textColor,
          background:  rawTheme.background  ?? DEFAULT_THEME.background,
          borderColor: rawTheme.borderColor ?? DEFAULT_THEME.borderColor,
        } : { ...DEFAULT_THEME, primary: d.primary_color || DEFAULT_THEME.primary };

        const rawSaved = d.saved_themes;
        setSavedThemes(Array.isArray(rawSaved) ? rawSaved : []);

        setForm({
          name:             normalizeLangField(d.name, d, 'name'),
          subtitle:         normalizeLangField(d.subtitle, d, 'subtitle'),
          team_name:        normalizeLangField(d.team_name, d, 'team_name'),
          subject:          normalizeLangField(d.subject, d, 'subject'),
          keywords:         normalizeKeywordsField(d.keywords),
          logo_url:         typeof d.logo_url === 'string' ? d.logo_url : '',
          og_image:         typeof d.og_image === 'string' ? d.og_image : '',
          website:          typeof d.website === 'string' ? d.website : '',
          support_email:    typeof d.support_email === 'string' ? d.support_email : '',
          default_language: normalizeDefaultLanguage(d.default_language ?? d.defaultLanguage),
          theme,
        });
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res: any = await api.updateAssociationParams({
        name:             form.name,
        subtitle:         form.subtitle,
        team_name:        form.team_name,
        subject:          form.subject,
        keywords:         form.keywords,
        primary_color:    form.theme.primary,
        logo_url:         form.logo_url,
        og_image:         form.og_image || form.logo_url,
        website:          form.website,
        support_email:    form.support_email,
        default_language: form.default_language,
        theme:            form.theme,
        saved_themes:     savedThemes,
      });
      if (res?.success) {
        const lang = normalizeDefaultLanguage(form.default_language);
        try {
          localStorage.setItem('selectedLanguage', lang);
          window.dispatchEvent(new CustomEvent('tanzeel-language-changed', { detail: lang }));
        } catch {
          /* ignore */
        }
        await refresh();
        setMessage({ type: 'success', text: lbl('Paramètres enregistrés ✓', 'تم الحفظ بنجاح ✓') });
      } else {
        setMessage({ type: 'error', text: res?.message || 'Erreur' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Erreur réseau' });
    }
    setSaving(false);
  };

  const setMulti = (field: keyof AssocForm, lang: 'ar' | 'fr' | 'en', value: string) => {
    setForm(prev => ({ ...prev, [field]: { ...(prev[field] as any), [lang]: value } }));
  };

  const setThemeColor = (key: keyof AppTheme, value: string) => {
    setForm(prev => ({ ...prev, theme: { ...prev.theme, id: null, [key]: value } }));
  };

  const selectPreset = (preset: typeof PRESET_THEMES[0]) => {
    setForm(prev => ({ ...prev, theme: { ...preset } }));
    setEditingSavedId(null);
  };

  const loadSavedTheme = (st: SavedCustomTheme) => {
    setEditingSavedId(st.id);
    setForm(prev => ({
      ...prev,
      theme: {
        id: null,
        primary:     st.primary,
        primaryText: st.primaryText,
        textColor:   st.textColor,
        headerBg:    st.headerBg,
        headerText:  st.headerText,
        accent:      st.accent,
        surface:     st.surface,
        background:  st.background  ?? DEFAULT_THEME.background,
        borderColor: st.borderColor ?? DEFAULT_THEME.borderColor,
      },
    }));
  };

  const saveCurrentAsSaved = () => {
    if (!saveDialogName.trim() || savedThemes.length >= 3) return;
    const newSaved: SavedCustomTheme = {
      id:          `custom-${Date.now()}`,
      name:        saveDialogName.trim(),
      primary:     form.theme.primary,
      primaryText: form.theme.primaryText,
      textColor:   form.theme.textColor,
      headerBg:    form.theme.headerBg,
      headerText:  form.theme.headerText,
      accent:      form.theme.accent,
      surface:     form.theme.surface,
      background:  form.theme.background,
      borderColor: form.theme.borderColor,
    };
    setSavedThemes(prev => [...prev, newSaved]);
    setEditingSavedId(newSaved.id);
    setShowSaveDialog(false);
    setSaveDialogName('');
  };

  const updateSavedTheme = () => {
    if (!editingSavedId) return;
    setSavedThemes(prev => prev.map(st => st.id === editingSavedId
      ? {
          ...st,
          primary:     form.theme.primary,
          primaryText: form.theme.primaryText,
          textColor:   form.theme.textColor,
          headerBg:    form.theme.headerBg,
          headerText:  form.theme.headerText,
          accent:      form.theme.accent,
          surface:     form.theme.surface,
          background:  form.theme.background,
          borderColor: form.theme.borderColor,
        }
      : st));
  };

  const deleteSavedTheme = (id: string) => {
    const name = savedThemes.find(st => st.id === id)?.name ?? '';
    const msg = isRTL ? `هل تريد حذف "${name}" ؟` : `Supprimer le thème "${name}" ?`;
    if (!window.confirm(msg)) return;
    setSavedThemes(prev => prev.filter(st => st.id !== id));
    if (editingSavedId === id) setEditingSavedId(null);
  };

  // ── Styles ─────────────────────────────────────────────────────────────────

  const s = {
    container:    { padding: isMobile ? '12px' : '24px', maxWidth: '860px' } as React.CSSProperties,
    title:        { fontSize: isMobile ? '16px' : '20px', fontWeight: '700', color: '#1a1a2e', marginBottom: isMobile ? '14px' : '24px', display: 'flex', alignItems: 'center', gap: '10px' } as React.CSSProperties,
    // Barre d'onglets défilante horizontalement sur mobile — pas de flex-wrap qui casse la mise en page
    tabs:         {
      display: 'flex', gap: '2px', marginBottom: isMobile ? '16px' : '28px',
      borderBottom: '2px solid #e0e0e0',
      overflowX: 'auto' as const, flexWrap: 'nowrap' as const,
      WebkitOverflowScrolling: 'touch' as any,
      scrollbarWidth: 'none' as any,   // Firefox
      msOverflowStyle: 'none' as any,  // IE/Edge
    },
    tab:          (active: boolean) => ({
      padding: isMobile ? '8px 10px' : '10px 20px',
      border: 'none', background: active ? '#f3e8ff' : 'none',
      borderRadius: isMobile ? '6px 6px 0 0' : '0',
      cursor: 'pointer',
      fontSize: isMobile ? '11px' : '14px',
      fontWeight: active ? '600' : '400',
      color: active ? '#5e35b1' : '#666',
      borderBottom: active ? '2px solid #5e35b1' : '2px solid transparent',
      marginBottom: '-2px', transition: 'all 0.2s',
      whiteSpace: 'nowrap' as const,   // empêche le texte de couper sur 2 lignes
      flexShrink: 0,                   // empêche les onglets de rétrécir
    }) as React.CSSProperties,
    section:      { marginBottom: '24px' } as React.CSSProperties,
    sectionTitle: { fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '12px', textTransform: 'uppercase' as const, letterSpacing: '0.5px' },
    row:          { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' } as React.CSSProperties,
    label:        { display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '4px' } as React.CSSProperties,
    input:        { width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' as const, direction: 'auto' as any },
    langBadge:    (lang: string): React.CSSProperties => ({ display: 'inline-block', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '700', marginInlineStart: '4px', background: lang === 'ar' ? '#e8f5e9' : lang === 'fr' ? '#e3f2fd' : '#fff8e1', color: lang === 'ar' ? '#2e7d32' : lang === 'fr' ? '#1565c0' : '#f57f17' }),
    saveBtn:      { backgroundColor: saving ? '#9e9e9e' : '#5e35b1', color: 'white', border: 'none', borderRadius: '8px', padding: '12px 32px', fontSize: '15px', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer', marginTop: '24px' } as React.CSSProperties,
    msg:          (type: 'success' | 'error'): React.CSSProperties => ({ padding: '12px 16px', borderRadius: '8px', marginTop: '16px', fontSize: '14px', background: type === 'success' ? '#e8f5e9' : '#ffebee', color: type === 'success' ? '#2e7d32' : '#c62828' }),
  };

  // Champ multilingue avec onglet langue partagé (fieldLang)
  const MultiField = ({
    field,
    label,
    compact = false,
  }: {
    field: keyof AssocForm;
    label: string;
    compact?: boolean;
  }) => (
    <div style={{ marginBottom: compact ? 0 : '10px' }}>
      <label style={{ fontSize: '11px', fontWeight: 600, color: '#666', display: 'block', marginBottom: '4px' }}>{label}</label>
      <input
        style={{ ...s.input, fontSize: '13px', padding: '7px 10px' }}
        value={(form[field] as LangTriple)[fieldLang]}
        onChange={(e) => setMulti(field, fieldLang, e.target.value)}
        dir={fieldLang === 'ar' ? 'rtl' : 'ltr'}
        placeholder={fieldLang === 'ar' ? 'بالعربية…' : fieldLang === 'fr' ? 'En français…' : 'In English…'}
      />
    </div>
  );

  const cb = {
    wrap: { display: 'flex', flexDirection: 'column' as const, gap: '12px' },
    block: {
      padding: '12px 14px',
      background: '#fafafa',
      borderRadius: '8px',
      border: '1px solid #eee',
    },
    blockTitle: {
      fontSize: '12px',
      fontWeight: 700,
      color: '#5e35b1',
      marginBottom: '8px',
    },
    hint: { fontSize: '11px', color: '#888', marginTop: '4px', lineHeight: 1.35 },
    input: { ...s.input, padding: '6px 10px', fontSize: '13px' },
    label: { ...s.label, marginBottom: '2px', fontSize: '11px' },
    twoCol: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      gap: '12px',
    } as React.CSSProperties,
    urlRow: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      gap: '10px',
      alignItems: 'center',
    } as React.CSSProperties,
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>⏳ Chargement...</div>;

  return (
    <>
    <style>{`
      .saved-theme-card:hover .saved-theme-delete { opacity: 1 !important; }
      .assoc-tabs::-webkit-scrollbar { display: none; }
    `}</style>
    <div style={s.container}>

      {/* Tabs */}
      <div style={s.tabs} className="assoc-tabs">
        {visibleTabs.map(key => {
          const tabConfig: Record<string, { key: string; label: string }> = {
            basic:    { key: 'basic',    label: lbl('ℹ️ Informations', 'ℹ️ المعلومات') },
            theme:    { key: 'theme',    label: lbl('🎨 Thème', '🎨 المظهر') },
            system:   { key: 'system',   label: lbl('⚙️ Système', '⚙️ النظام') },
            subjects: { key: 'subjects', label: lbl('📚 Matières', '📚 المواد') },
            landing:  { key: 'landing',  label: lbl('🏠 Page d\'accueil', '🏠 الصفحة الرئيسية') },
          };
          const tab = tabConfig[key];
          return (
            <button key={tab.key} style={s.tab(activeTab === tab.key)} onClick={() => setActiveTab(tab.key as any)}>
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── Informations ─────────────────────────────────────────────────── */}
      {activeTab === 'basic' && (
        <>
          {/* Sélecteur de langue partagé pour tous les champs multilingues */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '14px', background: '#f5f5f5', borderRadius: '8px', padding: '4px' }}>
            {(['ar', 'fr', 'en'] as const).map(lg => (
              <button key={lg} onClick={() => setFieldLang(lg)} style={{
                flex: 1, padding: '6px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: fieldLang === lg ? '700' : '400',
                background: fieldLang === lg ? 'white' : 'transparent',
                color: fieldLang === lg ? '#5e35b1' : '#888',
                boxShadow: fieldLang === lg ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                transition: 'all 0.15s',
              }}>
                {lg === 'ar' ? 'العربية' : lg === 'fr' ? 'Français' : 'English'}
              </button>
            ))}
          </div>

          {/* ── Champs qui dépendent de la langue ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
            <MultiField field="name"      label={lbl("Nom de l'association", 'اسم الجمعية')} />
            <MultiField field="subtitle"  label={lbl('Slogan', 'الشعار النصي')} />
            <MultiField field="team_name" label={lbl("Équipe", 'الفريق')} />
            <MultiField field="subject"   label={lbl('Sujet email par défaut', 'موضوع البريد الافتراضي')} />
            <MultiField field="keywords"  label={lbl('Mots-clés SEO', 'الكلمات المفتاحية')} />
          </div>

          {/* ── Champs indépendants de la langue ── */}
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '14px' }}>

            {/* Site web + Langue sur la même ligne */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 600, color: '#666', display: 'block', marginBottom: '4px' }}>{lbl('Site web', 'الموقع الإلكتروني')}</label>
                <input style={{ ...s.input, fontSize: '13px', padding: '7px 10px' }} value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} dir="ltr" placeholder="https://" />
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 600, color: '#666', display: 'block', marginBottom: '4px' }}>{lbl('Langue par défaut', 'اللغة الرئيسية')}</label>
                <select style={{ ...s.input, fontSize: '13px', padding: '7px 10px' }} value={normalizeDefaultLanguage(form.default_language)} onChange={(e) => setForm((p) => ({ ...p, default_language: e.target.value }))}>
                  <option value="ar">العربية</option>
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>

            {/* Logo · SEO · Email */}
            <div style={cb.wrap}>
              <div style={cb.twoCol}>
                <div style={cb.block}>
                  <div style={cb.blockTitle}>{lbl('Logo', 'الشعار')}</div>
                  <div style={cb.urlRow}>
                    <div>
                      <label style={cb.label}>{lbl('URL Cloudinary', 'رابط الشعار')}</label>
                      <input style={cb.input} value={form.logo_url} onChange={(e) => setForm((p) => ({ ...p, logo_url: e.target.value }))} dir="ltr" placeholder="https://res.cloudinary.com/..." />
                    </div>
                    {form.logo_url ? (
                      <img src={form.logo_url} alt="logo" style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #ddd' }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    ) : (
                      <div style={{ width: 48, height: 48, borderRadius: 6, border: '1px dashed #ddd', background: '#fff' }} />
                    )}
                  </div>
                </div>
                <div style={cb.block}>
                  <div style={cb.blockTitle}>SEO · {lbl('Email', 'البريد')}</div>
                  <label style={cb.label}>{lbl('Image OG', 'صورة المشاركة')}</label>
                  <input style={cb.input} value={form.og_image} onChange={(e) => setForm((p) => ({ ...p, og_image: e.target.value }))} dir="ltr" placeholder="https://... (1200×630)" />
                  <div style={cb.hint}>{lbl('* Partage Facebook/Twitter', '* مشاركة Facebook/Twitter')}</div>
                  <div style={{ marginTop: '8px' }}>
                    <label style={cb.label}>{lbl('Email de support', 'بريد الدعم')}</label>
                    <input style={cb.input} value={form.support_email} onChange={(e) => setForm((p) => ({ ...p, support_email: e.target.value }))} dir="ltr" placeholder="support@..." />
                    <div style={cb.hint}>{lbl('* Expéditeur noreply@domaine', '* الإرسال noreply@النطاق')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Thème ────────────────────────────────────────────────────────── */}
      {activeTab === 'theme' && (
        <>
          {/* Grille unifiée : presets + sauvegardés + bouton ajout */}
          <div style={s.section}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={s.sectionTitle}>{lbl('Thèmes', 'المظاهر')}</div>
              <span style={{ fontSize: '12px', color: '#888' }}>{lbl(`${savedThemes.length}/3 sauvegardés`, `${savedThemes.length}/3 محفوظ`)}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '12px', marginBottom: '8px' }}>

              {/* ── Presets ── */}
              {PRESET_THEMES.map(preset => {
                const isActive = form.theme.id === preset.id;
                return (
                  <button key={preset.id} onClick={() => selectPreset(preset)} style={{ border: isActive ? `3px solid ${preset.primary}` : '2px solid #e0e0e0', borderRadius: '12px', background: 'white', cursor: 'pointer', padding: '12px 8px', textAlign: 'center', transition: 'all 0.2s', boxShadow: isActive ? `0 0 0 2px ${preset.primary}33` : '0 1px 3px rgba(0,0,0,0.07)', position: 'relative' }}>
                    <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', height: '32px', marginBottom: '8px' }}>
                      <div style={{ flex: 1, background: preset.headerBg }} />
                      <div style={{ flex: 1, background: preset.primary }} />
                      <div style={{ flex: 1, background: preset.accent }} />
                      <div style={{ flex: 1, background: preset.surface, border: '1px solid #f0f0f0' }} />
                    </div>
                    <div style={{ fontSize: '16px', marginBottom: '4px' }}>{preset.emoji}</div>
                    <div style={{ fontSize: '12px', fontWeight: isActive ? '700' : '500', color: isActive ? preset.primary : '#444' }}>{isRTL ? preset.name.ar : preset.name.fr}</div>
                    {isActive && <div style={{ position: 'absolute', top: '6px', right: '6px', width: '18px', height: '18px', borderRadius: '50%', background: preset.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ color: 'white', fontSize: '10px', fontWeight: '800' }}>✓</span></div>}
                  </button>
                );
              })}

              {/* ── Sauvegardés ── */}
              {savedThemes.map(st => {
                const isActive = editingSavedId === st.id;
                return (
                  <div key={st.id} className="saved-theme-card" style={{ position: 'relative', border: isActive ? '3px solid #455a64' : '2px solid #e0e0e0', borderRadius: '12px', background: 'white', padding: '12px 8px', textAlign: 'center', boxShadow: isActive ? '0 0 0 2px #455a6433' : '0 1px 3px rgba(0,0,0,0.07)' }}>
                    <button onClick={() => deleteSavedTheme(st.id)} title={lbl('Supprimer', 'حذف')} className="saved-theme-delete" style={{ position: 'absolute', top: '4px', left: '4px', width: '18px', height: '18px', borderRadius: '50%', border: 'none', background: '#ffebee', color: '#c62828', cursor: 'pointer', fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, opacity: 0, transition: 'opacity 0.15s' }}>✕</button>
                    <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', height: '32px', marginBottom: '8px', cursor: 'pointer' }} onClick={() => loadSavedTheme(st)}>
                      <div style={{ flex: 1, background: st.headerBg }} />
                      <div style={{ flex: 1, background: st.primary }} />
                      <div style={{ flex: 1, background: st.accent }} />
                      <div style={{ flex: 1, background: st.surface, borderLeft: '1px solid #eee' }} />
                    </div>
                    <div style={{ fontSize: '12px', fontWeight: isActive ? '700' : '500', color: isActive ? '#455a64' : '#444', marginBottom: '6px', cursor: 'pointer', wordBreak: 'break-word' as const }} onClick={() => loadSavedTheme(st)}>{st.name}</div>
                    {isActive ? (
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        <button onClick={updateSavedTheme} title={lbl('Mettre à jour', 'تحديث')} style={{ fontSize: '11px', padding: '3px 8px', border: 'none', borderRadius: '4px', background: '#455a64', color: 'white', cursor: 'pointer', fontWeight: '700' }}>🔄</button>
                      </div>
                    ) : (
                      <button onClick={() => loadSavedTheme(st)} style={{ fontSize: '10px', padding: '3px 8px', border: '1px solid #ddd', borderRadius: '4px', background: 'white', color: '#555', cursor: 'pointer' }}>{lbl('Charger', 'تحميل')}</button>
                    )}
                    {isActive && <div style={{ position: 'absolute', top: '6px', right: '6px', width: '18px', height: '18px', borderRadius: '50%', background: '#455a64', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ color: 'white', fontSize: '10px', fontWeight: '800' }}>✓</span></div>}
                  </div>
                );
              })}

              {/* ── Carte "حفظ الألوان الحالية" ── */}
              {savedThemes.length < 3 && (
                showSaveDialog ? (
                  <div style={{ border: '2px solid #455a64', borderRadius: '12px', background: 'white', padding: '10px', display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'center' }}>
                    <input autoFocus style={{ ...s.input, margin: 0, fontSize: '12px', padding: '6px 10px' }} value={saveDialogName} onChange={e => setSaveDialogName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') saveCurrentAsSaved(); if (e.key === 'Escape') { setShowSaveDialog(false); setSaveDialogName(''); } }} placeholder={lbl('Nom du thème…', 'اسم المظهر…')} dir={isRTL ? 'rtl' : 'ltr'} />
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={saveCurrentAsSaved} disabled={!saveDialogName.trim()} style={{ flex: 1, padding: '6px 0', background: saveDialogName.trim() ? '#455a64' : '#ccc', color: 'white', border: 'none', borderRadius: '6px', cursor: saveDialogName.trim() ? 'pointer' : 'not-allowed', fontSize: '14px' }}>💾</button>
                      <button onClick={() => { setShowSaveDialog(false); setSaveDialogName(''); }} style={{ padding: '6px 10px', background: '#f5f5f5', color: '#555', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>✕</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => { setSaveDialogName(''); setShowSaveDialog(true); }} style={{ border: '2px dashed #bdbdbd', borderRadius: '12px', background: 'white', cursor: 'pointer', padding: '12px 8px', textAlign: 'center', color: '#888', minHeight: '100px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s' }}>
                    <span style={{ fontSize: '22px' }}>+</span>
                    <span style={{ fontSize: '11px', lineHeight: '1.5' }}>{lbl('Sauvegarder les couleurs actuelles', 'حفظ الألوان الحالية')}</span>
                  </button>
                )
              )}
            </div>

          </div>

          {/* Éditeur de couleurs */}
          <div style={s.section}>
            <div style={s.sectionTitle}>{lbl('Personnaliser les couleurs', 'تخصيص الألوان')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
              {COLOR_SLOTS.map(slot => (
                <div key={slot.key as string} style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: '8px', padding: '10px 12px' }}>
                  <label style={{ ...s.label, marginBottom: '2px' }}>{lbl(slot.labelFr, slot.labelAr)}</label>
                  {slot.hint && <div style={{ fontSize: '10px', color: '#aaa', marginBottom: '6px' }}>{slot.hint}</div>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <input
                        type="color"
                        value={form.theme[slot.key] as string}
                        onChange={e => setThemeColor(slot.key, e.target.value)}
                        style={{ width: '44px', height: '38px', padding: '2px', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', display: 'block' }}
                      />
                    </div>
                    <input
                      style={{ ...s.input, flex: 1, fontFamily: 'monospace', fontSize: '13px' }}
                      value={form.theme[slot.key] as string}
                      onChange={e => setThemeColor(slot.key, e.target.value)}
                      dir="ltr"
                      placeholder="#000000"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Aperçu du thème */}
          <div style={s.section}>
            <div style={s.sectionTitle}>{lbl('Aperçu en direct', 'معاينة مباشرة')}</div>
            <div style={{ borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', border: `1px solid ${form.theme.borderColor}` }}>

              {/* Header */}
              <div style={{ background: form.theme.headerBg, padding: '0 20px', display: 'flex', alignItems: 'center', gap: '12px', height: '52px' }}>
                <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: form.theme.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: form.theme.primaryText, fontSize: '14px', fontWeight: '800' }}>E</span>
                </div>
                <span style={{ color: form.theme.headerText, fontWeight: '700', fontSize: '14px', flex: 1 }}>
                  {lbl("Nom de l'association", 'اسم الجمعية')}
                </span>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {[lbl('Accueil', 'الرئيسية'), lbl('Cours', 'الدروس'), lbl('Résultats', 'النتائج')].map(n => (
                    <div key={n} style={{ color: form.theme.headerText, padding: '4px 10px', borderRadius: '4px', fontSize: '11px', opacity: 0.8 }}>{n}</div>
                  ))}
                  <div style={{ background: form.theme.primary, color: form.theme.primaryText, padding: '5px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: '700' }}>
                    {lbl('Connexion', 'دخول')}
                  </div>
                </div>
              </div>

              {/* Page body */}
              <div style={{ background: form.theme.background, padding: '16px', display: 'flex', gap: '12px', minHeight: '160px' }}>

                {/* Sidebar */}
                <div style={{ width: '110px', background: form.theme.surface, borderRadius: '8px', border: `1px solid ${form.theme.borderColor}`, padding: '10px 0', flexShrink: 0 }}>
                  {[
                    { label: lbl('Tableau', 'لوحة التحكم'), active: true },
                    { label: lbl('Élèves', 'الطلاب'),     active: false },
                    { label: lbl('Cours', 'الدروس'),       active: false },
                    { label: lbl('Résultats', 'النتائج'),  active: false },
                  ].map(item => (
                    <div key={item.label} style={{
                      padding: '7px 12px',
                      fontSize: '11px',
                      fontWeight: item.active ? '700' : '400',
                      color: item.active ? form.theme.primary : form.theme.textColor,
                      background: item.active ? `${form.theme.primary}18` : 'transparent',
                      borderLeft: item.active ? `3px solid ${form.theme.primary}` : '3px solid transparent',
                      cursor: 'pointer',
                    }}>{item.label}</div>
                  ))}
                </div>

                {/* Main content */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>

                  {/* Stat cards row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                    {[
                      { label: lbl('Élèves', 'الطلاب'),   value: '248', icon: '👤' },
                      { label: lbl('Cours actifs', 'الدروس'),  value: '12',  icon: '📚' },
                      { label: lbl('Résultats', 'النتائج'), value: '94%', icon: '🏆' },
                    ].map(card => (
                      <div key={card.label} style={{ background: form.theme.surface, borderRadius: '8px', border: `1px solid ${form.theme.borderColor}`, padding: '10px 12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                          <span style={{ fontSize: '14px' }}>{card.icon}</span>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: form.theme.accent }} />
                        </div>
                        <div style={{ fontSize: '16px', fontWeight: '800', color: form.theme.primary, lineHeight: 1 }}>{card.value}</div>
                        <div style={{ fontSize: '10px', color: form.theme.textColor, opacity: 0.7, marginTop: '3px' }}>{card.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Content card */}
                  <div style={{ background: form.theme.surface, borderRadius: '8px', border: `1px solid ${form.theme.borderColor}`, padding: '12px', flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                      <div style={{ width: '4px', height: '16px', background: form.theme.accent, borderRadius: '2px' }} />
                      <span style={{ fontSize: '12px', fontWeight: '700', color: form.theme.textColor }}>{lbl('Dernières activités', 'آخر الأنشطة')}</span>
                    </div>
                    {[85, 65, 92].map((w, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: `${form.theme.primary}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ fontSize: '9px', color: form.theme.primary, fontWeight: '700' }}>{i + 1}</span>
                        </div>
                        <div style={{ flex: 1, height: '6px', background: form.theme.borderColor, borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{ width: `${w}%`, height: '100%', background: i === 0 ? form.theme.primary : form.theme.accent, borderRadius: '3px' }} />
                        </div>
                        <span style={{ fontSize: '10px', color: form.theme.textColor, opacity: 0.6, width: '28px', textAlign: 'right' }}>{w}%</span>
                      </div>
                    ))}
                    <div style={{ marginTop: '10px' }}>
                      <button style={{ background: form.theme.primary, color: form.theme.primaryText, border: 'none', borderRadius: '6px', padding: '6px 14px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', marginRight: '6px' }}>
                        {lbl('Voir tout', 'عرض الكل')}
                      </button>
                      <button style={{ background: 'transparent', color: form.theme.primary, border: `1px solid ${form.theme.primary}`, borderRadius: '6px', padding: '6px 14px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>
                        {lbl('Exporter', 'تصدير')}
                      </button>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Système (paramètres backend) ─────────────────────────────────── */}
      {activeTab === 'system' && (
        <SystemParamsTab isRTL={isRTL} currentLanguage={currentLanguage} />
      )}

      {activeTab === 'subjects' && (
        <SubjectsSettingsTab isRTL={isRTL} currentLanguage={currentLanguage} canEdit={canEdit} />
      )}

      {/* ── Page d'accueil (sections paramétrables) ──────────────────────── */}
      {activeTab === 'landing' && (
        <LandingSectionsTab isRTL={isRTL} currentLanguage={currentLanguage} canEdit={canEdit} isSuperUser={isSuperAdmin} />
      )}

      {/* Bouton Enregistrer — masqué sur les onglets système, matières et landing */}
      {activeTab !== 'system' && activeTab !== 'subjects' && activeTab !== 'landing' && (
        <>
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? lbl('Enregistrement...', 'جارٍ الحفظ...') : lbl('💾 Enregistrer', '💾 حفظ الإعدادات')}
          </button>
          {message && <div style={s.msg(message.type)}>{message.text}</div>}
        </>
      )}
    </div>
    </>
  );
};
